import {
  LocalizationContextBuilder,
  MessageBundle,
  LocalizationContext,
} from '@amzn/arb-tools';
import { DEFAULT_LOCALE } from 'src/constants/localization';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import generatedTranslations from '../../build/translations';
import generatedArbManifest from '../../build/translations/arbManifest';

const LOCALIZATION_CONTEXTS = new Map<string, LocalizationContext>();

export const t = (
  bundle: MessageBundle,
  stringId: string,
  args?: Record<string, unknown>,
) => {
  if (!bundle || !stringId) {
    return stringId || '';
  }

  try {
    const result = !args ? bundle.getMessage(stringId) : bundle.formatMessage(stringId, args);

    if (!result) {
      ClientLogger.warn(ClientLog.TranslationNotFound, {
        stringId,
        url: window.location.href,
      });
      return stringId;
    }

    return result;
  } catch {
    ClientLogger.warn(ClientLog.TranslationNotFound, {
      stringId,
      url: window.location.href,
    });
    return stringId;
  }
};

/**
 * Resolves dynamic content that may already be localized (PolyD) or need translation (static config).
 * Use this instead of t() for fields like title/description that come from API responses.
 */
export const resolveContent = (
  bundle: MessageBundle,
  content: string,
  isLocalized: boolean,
) => (isLocalized ? content : t(bundle, content));

export const getLocalizationContext = (locale: string) => {
  let lc = LOCALIZATION_CONTEXTS.get(locale);

  if (!lc) {
    lc = new LocalizationContextBuilder()
      .withLocale(locale)
      .withDefaultLocale(DEFAULT_LOCALE)
      .build();

    LOCALIZATION_CONTEXTS.set(locale, lc);
  }

  return lc;
};

export const resolveBundleUrlWithFallbacks = (
  resourcePackPath: string | undefined,
  locale: string,
  bundle: string,
) => {
  if (resourcePackPath && generatedTranslations[resourcePackPath]) {
    return generatedTranslations[resourcePackPath];
  }

  if (!resourcePackPath && generatedArbManifest.endpointMap?.[locale]?.[bundle]) {
    return generatedTranslations[generatedArbManifest.endpointMap[locale][bundle]];
  }

  if (generatedArbManifest.endpointMap?.[DEFAULT_LOCALE]?.[bundle]) {
    return generatedTranslations[generatedArbManifest.endpointMap[DEFAULT_LOCALE][bundle]];
  }

  return undefined;
};

export const getMbmOptions = (
  arbManifest: { endpointMap: Record<string, Record<string, string>> },
  locale?: string,
) => {
  const resolvedLocale = locale
    ?? document?.querySelector('html')?.getAttribute('lang')
    ?? DEFAULT_LOCALE;

  const localizationContext = getLocalizationContext(resolvedLocale);

  return {
    arbManifest,
    defaultLocalizationContext: localizationContext,
    resolveBundleUrl: (
      resourcePackPath: string | undefined,
      bundleLocale: string,
      bundle: string,
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, bundleLocale, bundle),
  };
};
